<template>
  <div class="group" style="margin-bottom: 10px; margin-top: 10px;">
    <Checkbox v-model="idot" inputId="idot" binary/>
    <label for="slider" class="ml-2" >Add Info Dot</label>
  </div>
  <Divider />
  <div v-if="idot">

    <div class="group" style="padding-top:10px;">
      <label style="flex:2;">Text Color</label>
      <ColorButton :color="idotColor" @click="$refs.idotTextColorPickerVisible.toggle($event)" style="flex: 3;"/>
      <OverlayPanel ref="idotTextColorPickerVisible">
        <ColorPicker
            :modelValue="idotColor"
            @update:modelValue="value => idotColor = value"
        />
      </OverlayPanel>
    </div>

    <div class="group" style="padding-top:10px;">
      <label style="flex:2;">Background Color</label>
      <ColorButton :color="idotBackgroundColor" @click="$refs.idotBackgroundColorPickerVisible.toggle($event)" style="flex: 3;"/>
      <OverlayPanel ref="idotBackgroundColorPickerVisible">
        <ColorPicker
            :modelValue="idotBackgroundColor"
            @update:modelValue="value => idotBackgroundColor = value"
        />
      </OverlayPanel>
    </div>

    <div class="group" style="padding-top:10px;">
      <label style="flex:2;">Border Color</label>
      <ColorButton :color="idotBorderColor" @click="$refs.idotBorderColorPickerVisible.toggle($event)" style="flex: 3;"/>
      <OverlayPanel ref="idotBorderColorPickerVisible">
        <ColorPicker
            :modelValue="idotBorderColor"
            @update:modelValue="value => idotBorderColor = value"
        />
      </OverlayPanel>
    </div>

    <div style="padding-top:20px;">
      <div>Message</div>
      <Editor ref="editor" v-model="idotMessage" :modules="toolbarOptions" editorStyle="height: 150px"/>
    </div>

    <div class="group" style="margin-bottom: 10px; margin-top: 10px;">
      <Checkbox v-model="idotImage" inputId="idot" binary/>
      <label for="slider" class="ml-2" >Include Image</label>
    </div>

    <div style="margin-bottom: 10px; margin-top: 10px;" v-if="idotImage">
      <div class="group" style="margin-bottom: 10px;"><span class="label">Width</span> <InputNumber v-model="idotImageWidth" showButtons class="dimension-input"/> <Button class="dimension-input" v-if="idotImageWidth != null" icon="pi pi-trash" @click="idotImageWidth = null"></Button></div>
      <div class="group"><span class="label">Height</span> <InputNumber v-model="idotImageHeight" showButtons class="dimension-input"/> <Button class="dimension-input" v-if="idotImageHeight != null" icon="pi pi-trash" @click="idotImageHeight = null"></Button></div>
    </div>
  </div>
</template>

<script setup lang="ts">

import {computed, nextTick, onMounted, ref, watch} from 'vue'
import {useWidgetStore} from "@/store";
import {storeToRefs} from "pinia";
import ColorPicker from "@/components/ColorPicker/index.vue";
import ColorButton from "@/views/Editor/Toolbar/common/ColorButton.vue";
import InputNumber from "primevue/inputnumber";
import Button from "primevue/button";

const widgetStore = useWidgetStore()
const {selectedWidget} = storeToRefs(widgetStore)
const editor = ref(null);

onMounted(() => {
  setTimeout(() => {
    setupQuill()
  }, 100)
});

watch(selectedWidget, (newVal) => {
  if (newVal) {
    setTimeout(() => {
      setupQuill()
    }, 100)
  }
})

const setupQuill = () => {
  if (selectedWidget.value?.idotButton?.idotMessage) {
    const processedContent = processNewLines(selectedWidget.value?.idotButton?.idotMessage);
    idotMessage.value = processedContent
    nextTick(() => {
      const quillInstance = editor.value.quill
      const editorRoot = quillInstance.root;

      quillInstance.clipboard.dangerouslyPasteHTML(processedContent);
      editorRoot.classList.remove('ql-blank');
    })
  }
}

function processNewLines(content) {
  if (!/^<p>.*<\/p>$/s.test(content)) {
    content = `<p>${content}</p>`;
  }

  content = content.replace(/\n/g, '</p><p>');

  return content;
}

const toolbarOptions = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    ['link']
  ]
}

const setNestedPropertyValue = (propertyPath: string, value: any) => {
  const path = propertyPath.split('.')
  let target = selectedWidget.value
  for (let i = 0; i < path.length - 1; i++) {
    if (!target[path[i]]) target[path[i]] = {}
    target = target[path[i]]
  }
  target[path[path.length - 1]] = value
}

const idot = computed({
  get() {
    return selectedWidget.value?.idotButton?.idot ?? false
  },
  set(val) {
    setNestedPropertyValue('idotButton.idot', val)
  }
})

const idotMessage = computed({
  get() {
    return selectedWidget.value?.idotButton?.idotMessage ?? ''
  },
  set(val) {
    setNestedPropertyValue('idotButton.idotMessage', val)
  }
})

const idotColor = computed({
  get() {
    return selectedWidget.value?.idotButton?.style?.color ?? 'rgb(97, 118, 132)'
  },
  set(val) {
    setNestedPropertyValue('idotButton.style.color', val)
  }
})

const idotBackgroundColor = computed({
  get() {
    return selectedWidget.value?.idotButton?.style?.backgroundColor ?? 'rgb(255, 255, 255)'
  },
  set(val) {
    setNestedPropertyValue('idotButton.style.backgroundColor', val)
  }
})

const idotBorderColor = computed({
  get() {
    return selectedWidget.value?.idotButton?.style?.borderColor ?? 'rgb(97, 118, 132)'
  },
  set(val) {
    setNestedPropertyValue('idotButton.style.borderColor', val)
  }
})

const idotImage = computed({
  get() {
    return !!selectedWidget.value?.idotButton?.idotImage
  },
  set(val) {
    setNestedPropertyValue('idotButton.idotImage', val)
  }
})

const idotImageWidth = computed({
  get() {
    return selectedWidget.value?.idotButton?.idotImageWidth ?? null
  },
  set(val) {
    setNestedPropertyValue('idotButton.idotImageWidth', val)
  }
})

const idotImageHeight = computed({
  get() {
    return selectedWidget.value?.idotButton?.idotImageHeight ?? null
  },
  set(val) {
    setNestedPropertyValue('idotButton.idotImageHeight', val)
  }
})
</script>

<style scoped lang="scss">
.group {
  display: flex;
  align-items: center;
}

.label {
  padding-right: 20px;
  width: 60px;
  flex-shrink: 0;
}

:deep( .p-checkbox ) {
  margin-right: 10px;
}

.dimension-input :deep(.p-inputtext) { width: 80px; }
.dimension-input :deep(.p-button),
:deep(.p-button.dimension-input){
  background: #fff;
  border: 1px solid #ccc;
  color: black;
}

:deep(.p-editor-toolbar) {
  display: none !important;
}
</style>